import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent, Badge } from '../../../shared/components/ui';
import {
  formatCurrency,
  formatDate,
  formatSqFt,
  formatPercent,
} from '../../../shared/utils/formatters';

const outcomeStyles = {
  profitable: { variant: 'green', label: 'Profitable' },
  marginal: { variant: 'yellow', label: 'Marginal' },
  loss: { variant: 'red', label: 'Loss' },
};

function JobCard({ job, onClick }) {
  const outcome = outcomeStyles[job.outcome] || outcomeStyles.marginal;

  return (
    <div
      className="p-4 border border-gray-200 dark:border-gray-800 rounded-lg hover:border-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all cursor-pointer"
      onClick={onClick}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{job.projectName}</h4>
          <p className="text-xs text-gray-500 dark:text-gray-400">{job.jobType} • {job.location}</p>
        </div>
        <Badge variant={outcome.variant} size="sm">{outcome.label}</Badge>
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-3 text-xs">
        <div>
          <span className="text-gray-500 dark:text-gray-400">Final Cost:</span>
          <span className="ml-1 font-medium text-gray-900 dark:text-gray-100">{formatCurrency(job.finalCost)}</span>
        </div>
        <div>
          <span className="text-gray-500 dark:text-gray-400">$/sq ft:</span>
          <span className="ml-1 font-medium text-gray-900 dark:text-gray-100">${job.costPerSqFt?.toFixed(2)}</span>
        </div>
        <div>
          <span className="text-gray-500 dark:text-gray-400">Size:</span>
          <span className="ml-1 text-gray-700 dark:text-gray-200">{formatSqFt(job.squareFootage)}</span>
        </div>
        <div>
          <span className="text-gray-500 dark:text-gray-400">Margin:</span>
          <span className={`ml-1 font-medium ${job.margin >= 10 ? 'text-green-600 dark:text-green-400' : job.margin >= 5 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'}`}>
            {formatPercent(job.margin)}
          </span>
        </div>
      </div>

      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
        <span className="text-xs text-gray-400 dark:text-gray-500">Completed {formatDate(job.completedDate)}</span>
        <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
          Referenced {job.timesReferenced}x recently
        </span>
      </div>
    </div>
  );
}

export function ReferencedJobs({ jobs = [], onViewAll }) {
  const navigate = useNavigate();

  const handleJobClick = (jobId) => {
    navigate(`/jobs/${jobId}`);
  };

  if (jobs.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <svg className="w-5 h-5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          Recently Referenced Jobs
        </CardTitle>
        {onViewAll && (
          <button
            onClick={onViewAll}
            className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 font-medium"
          >
            View all →
          </button>
        )}
      </CardHeader>
      <CardContent>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Historical jobs frequently used by the AI for price comparisons
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {jobs.slice(0, 5).map((job) => (
            <JobCard
              key={job.id}
              job={job}
              onClick={() => handleJobClick(job.id)}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default ReferencedJobs;
